import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/card";
import { Label } from "../../../../ui/label";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";
import { Switch } from "../../../../ui/switch";
import ModelSelect from "../../../../configuration/ModelSelect";
import { DatabaseIcon, LayersIcon, SlidersHorizontalIcon } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { GenerationConfig, GENERATION_SIZE_TIERS } from "./types";
import ProdSeedPicker from "../ProdSeedPicker";

interface StepSetupProps {
  config: GenerationConfig;
  onChange: (partial: Partial<GenerationConfig>) => void;
  intentGroupId?: number;
}

const StepSetup: React.FC<StepSetupProps> = ({ config, onChange, intentGroupId }) => {
  const isStructured = config.generation_mode === "structured";
  const useProdSeeds = config.generation_use_prod_seeds;

  const handleSizeTierChange = (tierId: string) => {
    const tier = GENERATION_SIZE_TIERS.find((t) => t.id === tierId);
    if (!tier) return;
    onChange({
      generation_size_tier: tierId,
      generation_size: tier.id === "custom" ? config.generation_size : tier.count,
    });
  };

  return (
    <div className="space-y-6">
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <LayersIcon className="w-5 h-5 text-maitai-green" />
            Generation mode
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => onChange({ generation_mode: "open_ended" })}
              className={cn(
                "p-3 rounded-lg border-2 text-left transition-colors",
                !isStructured ? "border-maitai-green bg-maitai-green/5" : "border-border hover:border-maitai-green/50"
              )}
            >
              <div className="text-sm font-gc-med text-foreground">Freeform</div>
              <div className="text-xs text-muted-foreground mt-1">
                Diverse prompts across domains. Best for general knowledge distillation.
              </div>
            </button>
            <button
              type="button"
              onClick={() => onChange({ generation_mode: "structured" })}
              className={cn(
                "p-3 rounded-lg border-2 text-left transition-colors",
                isStructured ? "border-maitai-green bg-maitai-green/5" : "border-border hover:border-maitai-green/50"
              )}
            >
              <div className="text-sm font-gc-med text-foreground">Structured</div>
              <div className="text-xs text-muted-foreground mt-1">
                Define templates with {"{{placeholders}}"}. Best for task-specific datasets.
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <LayersIcon className="w-5 h-5 text-maitai-green" />
            Dataset size
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {GENERATION_SIZE_TIERS.map((tier) => (
              <button
                key={tier.id}
                type="button"
                onClick={() => handleSizeTierChange(tier.id)}
                className={cn(
                  "p-3 rounded-lg border-2 text-left transition-colors",
                  config.generation_size_tier === tier.id
                    ? "border-maitai-green bg-maitai-green/5"
                    : "border-border hover:border-maitai-green/50"
                )}
              >
                <div className="text-sm font-gc-med text-foreground">{tier.label}</div>
                {tier.id !== "custom" && (
                  <div className="text-xs text-muted-foreground mt-1">~{tier.count.toLocaleString()} examples</div>
                )}
                <div className="text-xs text-muted-foreground mt-1">{tier.description}</div>
              </button>
            ))}
          </div>

          {config.generation_size_tier === "custom" && (
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">
                Number of examples <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                min={10}
                max={100000}
                value={config.generation_size || ""}
                onChange={(e) => onChange({ generation_size: parseInt(e.target.value) || 0 })}
                placeholder="e.g. 5000"
              />
              <p className="text-xs text-muted-foreground">Minimum 10, maximum 100,000</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <DatabaseIcon className="w-5 h-5 text-maitai-green" />
            Dataset seeds
          </CardTitle>
          <p className="text-xs text-muted-foreground font-gc-reg pt-1">
            Production traffic or synthetic seeding — the Prompts step is where you shape the user message.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-sm font-gc-med text-foreground">Use production data as seeds</Label>
              <p className="text-xs text-muted-foreground font-gc-reg">
                Select real requests from your production traffic as starting points.
              </p>
            </div>
            <Switch
              checked={useProdSeeds}
              onCheckedChange={(checked) => onChange({
                generation_use_prod_seeds: !!checked,
                generation_prod_seed_ids: checked ? config.generation_prod_seed_ids : [],
              })}
            />
          </div>

          {useProdSeeds && intentGroupId && (
            <div className="pt-3 border-t border-border space-y-2">
              <ProdSeedPicker
                intentGroupId={intentGroupId}
                selectedRequestIds={config.generation_prod_seed_ids || []}
                onSelectionChange={(ids) => onChange({ generation_prod_seed_ids: ids })}
                minRequired={5}
              />
              {(config.generation_prod_seed_ids || []).length < 5 && (
                <p className="text-sm text-amber-600 dark:text-amber-500 font-gc-reg">
                  Select at least 5 production seed examples to continue.
                </p>
              )}
            </div>
          )}

          {useProdSeeds && !intentGroupId && (
            <div className="pt-3 border-t border-border">
              <p className="text-sm text-amber-500 font-gc-reg">
                Intent group information is not available. Production seed selection requires
                an intent group to be configured in the Base Type step.
              </p>
            </div>
          )}

          {!useProdSeeds && (
            <div className="pt-3 border-t border-border space-y-3">
              <Label className="text-sm font-gc-med text-foreground flex items-center gap-2">
                Concept seeding model
                <span className="text-xs text-muted-foreground font-gc-reg">(optional)</span>
              </Label>
              <ModelSelect
                value={config.generation_seed_model}
                onChange={(value) => onChange({ generation_seed_model: value })}
                placeholder={"None — we will pick the best setup"}
                label=""
                showEmptyOption={true}
                modelType="server"
              />
              <p className="text-xs text-muted-foreground font-gc-reg">
                Override the model used to seed initial concepts and evolve prompts.
                Leave as &quot;None&quot; and we&apos;ll automatically select the best model for the job.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <SlidersHorizontalIcon className="w-5 h-5 text-maitai-green" />
            Generation guidance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-xs text-muted-foreground">
            Optional knobs that steer how seeds evolve and how hard examples should be — separate from where raw seeds come from.
          </p>
          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">
              {useProdSeeds ? "Evolution instructions" : "Seed instructions"}
              <span className="text-xs text-muted-foreground font-gc-reg ml-2">(optional)</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              {useProdSeeds
                ? "Guide how production seeds are evolved and diversified."
                : "Constraints or instructions for the model that generates seed prompts."}
            </p>
            <Textarea
              value={config.generation_seed_instructions}
              onChange={(e) => onChange({ generation_seed_instructions: e.target.value })}
              placeholder={
                useProdSeeds
                  ? "e.g. Focus on edge cases and ambiguous inputs. Vary the complexity of product descriptions."
                  : "e.g. Only generate Python code examples. Avoid trivial hello-world problems."
              }
              rows={3}
            />
          </div>
          <div className="pt-3 border-t border-border space-y-3">
            <Label className="text-sm font-gc-med text-foreground">
              Difficulty progression
              <span className="text-xs text-muted-foreground font-gc-reg ml-2">(optional)</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Describe how examples should vary in difficulty across the dataset.
            </p>
            <Textarea
              value={config.generation_difficulty_cues}
              onChange={(e) => onChange({ generation_difficulty_cues: e.target.value })}
              placeholder={"Easy: Clear, unambiguous inputs\nMedium: Subtle cases requiring careful reading\nHard: Ambiguous inputs, edge cases, adversarial examples"}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StepSetup;
